import { useAtom } from "jotai";
import _ from "lodash";
import moment from "moment";
import { useCallback, useContext, useMemo } from "react";
import { AvisConstants } from "../../../../../data/avisConstants";
import {
  FormikUtils,
  TimeUtils,
  convertFileToBase64,
} from "../../../../../utils";
import { AvisContext } from "../../../context";
import { IModalConditionalResult } from "../../modal";
import { atomIsModalOpen, atomModalEditItemIndex, atomRowKeys } from "../atoms";
import {
  IAvisEditableListFieldArrayProps,
  IEditableListRowState,
  IEditableRowState,
} from "../types";
import { AvisEditableListRowClass } from "./AvisEditableListRowClass";

export function AvisEditableListFields<
  T extends object,
  P extends IEditableListRowState,
>(props: Readonly<IAvisEditableListFieldArrayProps<T, P>>) {
  const {
    bag,
    bagField,
    fields,
    view,
    disabled,
    customAddEditConfig,
    arrayHelpers,
    initValue,
    hideEdit,
    confirmBeforeDeleteOnAnyData,
    confirmBeforeDelete,
  } = props;

  const { res } = useContext(AvisContext);

  const bagFieldArray = useMemo(
    () => bag.values[bagField] as P[],
    [bag, bagField],
  );

  const [rowKeys, setRowKeys] = useAtom(atomRowKeys);
  const [, setModalEditItemIndex] = useAtom(atomModalEditItemIndex);
  const [, setIsModalOpen] = useAtom(atomIsModalOpen);

  const customEditCallback = useCallback(
    (index: number) => {
      setModalEditItemIndex(index);
      setIsModalOpen(true);
    },
    [setIsModalOpen, setModalEditItemIndex],
  );

  const confirmBeforeDeleteInner = useCallback(
    (item: P): IModalConditionalResult | undefined => {
      const mustConfirm = confirmBeforeDelete
        ? confirmBeforeDelete(item)
        : undefined;

      if (mustConfirm) return mustConfirm;

      //default case - check if row value is different than init value
      const changedRow = JSON.stringify(item) != JSON.stringify(initValue);
      const mustConfirm_default =
        confirmBeforeDeleteOnAnyData && changedRow
          ? {
              content: res("message_DeleteRow"),
              title: res("title_Confirm"),
              mustShow: true,
            }
          : undefined;

      return mustConfirm_default;
    },
    [confirmBeforeDelete, confirmBeforeDeleteOnAnyData, initValue, res],
  );

  const handleRowDelete = useCallback(
    (i: number) => {
      FormikUtils.deleteFieldValue(arrayHelpers, i, setRowKeys);
    },
    [arrayHelpers, setRowKeys],
  );

  const onStateChange = useCallback(
    (index: number, toState: IEditableRowState) => {
      const rowToChange = bagFieldArray[index];
      switch (toState) {
        case "View": {
          if (!rowToChange.rowState || rowToChange.rowState === "Add") {
            handleRowDelete(index);
          } else if (rowToChange.rowState === "Edit") {
            bag.setFieldValue(
              `${bagField.toString()}.${index}`,
              rowToChange.initialRowValue,
            );
          } else if (rowToChange.rowState === "Delete") {
            bag.setFieldValue(
              `${bagField.toString()}.${index}.rowState`,
              toState,
            );
          }
          break;
        }
        case "Edit": {
          //Moving on edit state -> save initialValue in same row
          bag.setFieldValue(`${bagField.toString()}.${index}`, {
            ...rowToChange,
            initialRowValue: rowToChange,
            rowState: toState,
          });
          break;
        }
        case "Delete": {
          bag.setFieldValue(
            `${bagField.toString()}.${index}.rowState`,
            toState,
          );
          break;
        }
      }
    },
    [bag, bagField, bagFieldArray, handleRowDelete],
  );

  const onChangeHandler = useCallback(
    async (e: any) => {
      let ctrlName = "";
      let ctrlValue = "";

      if (!e.target) return;

      if (!e.target.name) {
        // DotsInputText change event handling
        const inputControl = e.originalEvent.target;

        ctrlName = inputControl.name;
        ctrlValue = e.target.value;

        if (ctrlName) bag.setFieldValue(ctrlName, ctrlValue);

        return;
      }

      ctrlName = e.target.name;

      if (e.target.type === "file") {
        //File change event handling
        //get file base64
        if (!e.target.files[0]) return;

        const fileObj = e.target.files[0] as File;

        convertFileToBase64(fileObj).then((fileContent) => {
          ctrlValue = (fileContent as string).split(",")[1];
          bag.setFieldValue(ctrlName, fileObj.name);
          bag.setFieldValue(
            ctrlName.replace("fileName", "base64Data"),
            ctrlValue,
          );
        });

        return;
      }

      if (TimeUtils.isDate(e.value)) {
        //Arbitrāža ar datumiem
        //Avis vienmēr dod servera lokālos laikus ISO formātā,
        //Dots ui kalendārs dod pārlūka lokālo laiku.
        //Lai saglabātu pareizo laiku, jāpārveido uz UTC un formatējam ISO formātā bez laika zonas
        ctrlValue = e.value
          ? moment(e.value).utc(true).format(AvisConstants.date_format_ISO)
          : "";
      } else if (e.value) {
        ctrlValue = e.value; // AvisDropdown
      } else {
        ctrlValue = e.target.value; //AvisInputText, DotsCheckbox
      }

      if (ctrlName) bag.setFieldValue(ctrlName, ctrlValue);
    },
    [bag],
  );

  const renderFields = useMemo(() => {
    if (bagFieldArray.length == 0) return;

    return bagFieldArray.map((cb, i) => (
      <AvisEditableListRowClass<P>
        {...props}
        prefix={bagField.toString()}
        index={i}
        value={cb}
        arrayLength={bagFieldArray.length}
        key={rowKeys?.[i] ?? _.uniqueId()}
        fields={fields}
        errors={bag.errors[bagField] as any}
        disabled={disabled}
        customEditCallback={
          customAddEditConfig?.renderForm && customEditCallback
        }
        hideEdit={hideEdit}
        onChange={onChangeHandler}
        confirmBeforeDelete={confirmBeforeDeleteInner}
        res={res}
        onStateChange={onStateChange}
      />
    ));
  }, [
    bag.errors,
    bagField,
    bagFieldArray,
    confirmBeforeDeleteInner,
    customAddEditConfig?.renderForm,
    customEditCallback,
    disabled,
    fields,
    hideEdit,
    onChangeHandler,
    onStateChange,
    props,
    res,
    rowKeys,
  ]);

  if (view === "description-list")
    return (
      <div className="avis-editable-description-list-content">
        {renderFields}
      </div>
    );

  return <tbody>{renderFields}</tbody>;
}

export default AvisEditableListFields;
